import { Tab, Tabs } from "@material-ui/core";
import * as _ from "lodash";
import * as React from "react";
import { FixtureDetailed, PlayerExpanded, TeamStat} from "../model/FixtureDetailed";
import { FixtureScoreboardHeaderView } from "./FixtureScoreboardHeaderView";
import { FixtureScoreboardView } from "./FixtureScoreboardView";
import { TabPanel } from "./TabPanel";

export interface Props {
  fixture: FixtureDetailed;
  isLiveFixture: boolean;
}

export interface State {
  value: number;
  index: number;
}

export class FixtureMapTabsView extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);
    this.props.fixture.maps.forEach((map, mapNumber) => {
      map.teamStats.flatMap(teamStat => teamStat.players).flatMap(player => player.clutches).filter(clutch => clutch !== undefined).forEach(clutch => clutch.mapNumber = clutch.mapNumber || mapNumber+1);
    });
    this.state = this.getInitialState();
  }

  private getInitialState() {
    return { value: 0, index: 0 };
  }

  private handleChange(event, newValue) {
    this.setState({ ...this.state, value: newValue });
  }

  // Fixtures with the teamswap bug might not have scoreboards in the correct order
  private getCombinedStats(teamID: number, combinedPlayerStats: PlayerExpanded[]) : TeamStat | undefined {
    const groupedItems: PlayerExpanded[] = [];
    combinedPlayerStats.forEach((e) => {
      if (groupedItems.findIndex((player) => player.playerId === e.playerId) < 0) {
        const playerEntries: PlayerExpanded[] = combinedPlayerStats.filter((player) => player.playerId === e.playerId);
        const combinedPlayer: PlayerExpanded = {
          playerId: e.playerId,
          name: e.name,
          kast: undefined as unknown,
          adr: undefined as unknown,
          kills: playerEntries.map((player) => player.kills).reduce((a, b) => a + b),
          deaths: playerEntries.map((player) => player.deaths).reduce((a, b) => a + b),
          assists: playerEntries.map((player) => player.assists).reduce((a, b) => a + b),
          suicides: playerEntries.map((player) => player.suicides).reduce((a, b) => a + b),
          headshots: playerEntries.map((player) => player.headshots).reduce((a, b) => a + b),
          entryKills: playerEntries.map((player) => player.entryKills).reduce((a, b) => a + b),
          flash_assists: playerEntries.map((player) => player.flash_assists).reduce((a, b) => a + b),
          kpr: undefined as unknown,
          dpr: undefined as unknown,
          clutches: playerEntries.map(player => player.clutches)
            .filter(clutches => clutches != undefined)
            .filter(clutches => clutches.length)
            .flatMap((_clutches, i) => {
              return _clutches.map(clutch => {
                return {type: clutch.type, round: clutch.round, mapNumber: i+1}})
          }),

          oneVersusXs: _.chain(playerEntries)
            .map(player => player.oneVersusXs)
            .flatMap(clutches => clutches ? [...clutches!] : [])
            .value(),
          multiKills: _.chain(playerEntries)
            .map(player => player.multiKills)
            .flatMap(multiKills => multiKills ? [...multiKills!] : [])
            .value(),
          entryKillClutches: _.chain(playerEntries)
            .map(player => player.entryKillClutches)
            .flatMap(entryKillClutches => entryKillClutches ? [...entryKillClutches!] : [])
            .value(),
        } as PlayerExpanded;
        groupedItems.push(combinedPlayer);
      }
    });

    const combinedTeamStats: TeamStat = {
      teamId: teamID,
      kills: _.sumBy(combinedPlayerStats, player => player.kills),
      deaths: _.sumBy(combinedPlayerStats, player => player.deaths),
      assists: _.sumBy(combinedPlayerStats, player => player.assists),
      suicides: _.sumBy(combinedPlayerStats, player => player.suicides),
      headshots: _.sumBy(combinedPlayerStats, player => player.headshots),
      entryKills: _.sumBy(combinedPlayerStats, player => player.entryKills),
      flash_assists: _.sumBy(combinedPlayerStats, player => player.flash_assists),
      startSide: "",
      players: _(groupedItems).sortBy(player => player.kills).reverse().value(),
    };
    return combinedTeamStats;
  }

  private getCombinedTeamStats(stats: TeamStat[]): TeamStat | undefined {
    if (!stats || stats.filter(teamStat => !teamStat).length > 0) {
      return undefined;
    }

    const combinedPlayerStats: PlayerExpanded[] = stats.flatMap(teamStat => teamStat.players);
    const groupedItems: PlayerExpanded[] = [];
    combinedPlayerStats.forEach((e) => {
      if (groupedItems.findIndex((player) => player.playerId === e.playerId) < 0) {
        const playerEntries: PlayerExpanded[] = combinedPlayerStats.filter((player) => player.playerId === e.playerId);
        const combinedPlayer: PlayerExpanded = {
          playerId: e.playerId,
          name: e.name,
          kast: undefined as unknown,
          adr: undefined as unknown,
          kills: playerEntries.map((player) => player.kills).reduce((a, b) => a + b),
          deaths: playerEntries.map((player) => player.deaths).reduce((a, b) => a + b),
          assists: playerEntries.map((player) => player.assists).reduce((a, b) => a + b),
          suicides: playerEntries.map((player) => player.suicides).reduce((a, b) => a + b),
          headshots: playerEntries.map((player) => player.headshots).reduce((a, b) => a + b),
          entryKills: playerEntries.map((player) => player.entryKills).reduce((a, b) => a + b),
          flash_assists: playerEntries.map((player) => player.flash_assists).reduce((a, b) => a + b),
          kpr: undefined as unknown,
          dpr: undefined as unknown,
          clutches: _.chain(playerEntries)
            .flatMap(player => player.clutches)
            .value(),
          oneVersusXs: _.chain(playerEntries)
            .map(player => player.oneVersusXs)
            .flatMap(clutches => clutches || [])
            .value(),
          multiKills: _.chain(playerEntries)
            .map(player => player.multiKills)
            .flatMap(multiKills => multiKills || [])
            .value(),
          entryKillClutches: _.chain(playerEntries)
            .map(player => player.entryKillClutches)
            .flatMap(entryKillClutches => entryKillClutches || [])
            .value(),
        } as PlayerExpanded;
        if(combinedPlayer.clutches.filter(clutch => clutch === undefined).length !== 0) {
          combinedPlayer.clutches = undefined!;
        }
        groupedItems.push(combinedPlayer);
      }
    });

    const combinedTeamStats: TeamStat = {
      teamId: stats[0]?.teamId,
      kills: _.sumBy(stats, teamStat => teamStat.kills),
      deaths: _.sumBy(stats, teamStat => teamStat.deaths),
      assists: _.sumBy(stats, teamStat => teamStat.assists),
      suicides: _.sumBy(stats, teamStat => teamStat.suicides),
      headshots: _.sumBy(stats, teamStat => teamStat.headshots),
      entryKills: _.sumBy(stats, teamStat => teamStat.entryKills),
      flash_assists: _.sumBy(stats, teamStat => teamStat.flash_assists),
      startSide: "",
      players: _(groupedItems).sortBy(player => player.kills).reverse().value(),
    };
    return combinedTeamStats;
  }

  render() {
    const allPlayers: PlayerExpanded[] = this.props.fixture.maps.flatMap(map => map.teamStats.flatMap(teamStat => teamStat.players));
    const allPlayerNames : string[] = [...new Set(this.props.fixture.maps.flatMap(x => x.teamStats).flatMap(x => x.players).map(x => x.name))];
    const groupedTeamStats : TeamStat[][] = Object.values(_(this.props.fixture.maps.flatMap(map => map.teamStats)).groupBy(teamStat => teamStat.teamId).value());
    const playerTeamPairs : {playerName: string; teams: TeamStat[][]}[] = allPlayerNames.map(playerName => { return {
      playerName : playerName,
      teams: groupedTeamStats.filter(teamStatList =>
        teamStatList.find(teamStat =>
          teamStat.players.find(player =>
            playerName === player.name
          )
        )
      )
    }}
    );
    const useFallback : boolean = playerTeamPairs.find(player => player.teams.length > 1) ? true : false;    
    let getTeamStat : (teamId) => TeamStat;
    if(useFallback) {
      getTeamStat = (teamIndex) => this.getCombinedStats(this.props.fixture.maps[0].teamStats[teamIndex].teamId, 
        allPlayers.filter(player =>
          this.props.fixture.maps[0].teamStats[teamIndex].players.flatMap(teamstatPlayer => teamstatPlayer.name)
          .filter(playerName => playerName === player.name).length > 0))!;
    } else {
      getTeamStat = (teamIndex) => this.getCombinedTeamStats(this.props.fixture.maps.filter(map => map.teamStats?.length > 0)
        .map((map) => map.teamStats.filter(teamStat => teamStat.teamId === this.props.fixture.participants[teamIndex].id)[0]))!;
    }

    return (
      <div>
        <Tabs
          value={this.state.value}
          onChange={(e, i) => this.handleChange(e, i)}
          indicatorColor="primary"
          textColor="primary"
          scrollable={true}
          scrollButtons="auto"
        >
          <Tab disableRipple label="All maps" />
          {this.props.fixture.maps.map((map, i) => {
            return (
              <Tab key={i} disableRipple label={"Map " + (i + 1) + ": " + ((map.mapName?.startsWith("de_") || map.mapName?.startsWith("cs_")) ? map.mapName.substring(3) : map?.mapName)} />
            );
          })}
        </Tabs>
        <TabPanel value={this.state.value} index={0}>
          <FixtureScoreboardHeaderView
            roundScoresList={this.props.fixture.maps.map((map) => map.roundScores)}
            participants={this.props.fixture.participants}
          />
          <div className="scoreboard-container">
            <FixtureScoreboardView teamStats={getTeamStat(0)} teamName={this.props.fixture.participants[0].name} isLiveFixture={this.props.isLiveFixture} />
            <FixtureScoreboardView teamStats={getTeamStat(1)} teamName={this.props.fixture.participants[1].name} isLiveFixture={this.props.isLiveFixture} />
          </div>
        </TabPanel>
        {this.props.fixture.maps.map((map, i) => {
          return (
            <TabPanel key={i} value={this.state.value} index={i + 1}>
              <FixtureScoreboardHeaderView roundScoresList={[map.roundScores]} participants={this.props.fixture.participants} />
              <div className="scoreboard-container">
                <FixtureScoreboardView teamName={this.props.fixture.participants[0].name} teamStats={map.teamStats.filter(teamStat => teamStat.teamId === this.props.fixture.participants[0].id)[0]} isLiveFixture={this.props.isLiveFixture} />
                <FixtureScoreboardView teamName={this.props.fixture.participants[1].name} teamStats={map.teamStats.filter(teamStat => teamStat.teamId === this.props.fixture.participants[1].id)[0]} isLiveFixture={this.props.isLiveFixture} />
              </div>
            </TabPanel>
          );
        })}
      </div>
    );
  }
}